import { useEffect, useState, useCallback } from "react";
import { Loader2, HardDrive, Upload, ExternalLink, RefreshCw, Unplug, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Button } from "@/components/ui/button";

export function DriveManager() {
  const [status, setStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFolder, setUploadFolder] = useState("Rapports");
  const SUBFOLDERS = ["RH", "Finances", "Rapports", "Ventes", "Patients"];

  const loadFiles = useCallback(async () => {
    setLoadingFiles(true);
    try { const { data } = await api.get("/admin/drive/files"); setFiles(data.files || []); }
    catch (e) { toast.error(apiError(e)); } finally { setLoadingFiles(false); }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/drive/status");
      setStatus(data);
      if (data.connected) loadFiles();
    } catch (e) { toast.error(apiError(e)); }
  }, [loadFiles]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("drive");
    if (q === "connected") toast.success("Google Drive connecté");
    if (q === "error") toast.error("Échec de la connexion Google Drive");
    loadStatus();
  }, [loadStatus]);

  const connect = async () => {
    try { const { data } = await api.get("/admin/drive/connect"); window.location.href = data.auth_url; }
    catch (e) { toast.error(apiError(e)); }
  };
  const disconnect = async () => {
    if (!window.confirm("Déconnecter Google Drive ?")) return;
    try { await api.post("/admin/drive/disconnect"); toast.success("Déconnecté"); setFiles([]); loadStatus(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("folder", uploadFolder);
      const { data } = await api.post("/admin/drive/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Fichier archivé dans « ${data.folder || uploadFolder} »`); loadFiles();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  if (!status) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" /></div>;

  if (!status.enabled) {
    return <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800" data-testid="drive-disabled">Google Drive n'est pas configuré (clés OAuth manquantes).</div>;
  }

  return (
    <div data-testid="drive-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${status.connected ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}><HardDrive size={20} /></div>
          <div>
            <p className="font-display text-base font-semibold text-navy">Archivage Google Drive</p>
            {status.connected
              ? <p className="text-xs text-slate-500">Connecté · {status.email} · Dossier <b>{status.folder_name}</b></p>
              : <p className="text-xs text-slate-500">Connectez votre compte pour archiver automatiquement dans « {status.folder_name} ».</p>}
          </div>
        </div>
        {status.connected
          ? <div className="flex gap-2">
              <Button variant="outline" onClick={loadFiles} className="rounded-full text-xs" data-testid="drive-refresh"><RefreshCw size={14} className="mr-1" /> Actualiser</Button>
              <Button variant="outline" onClick={disconnect} className="rounded-full border-red-200 text-xs text-red-600 hover:bg-red-50" data-testid="drive-disconnect"><Unplug size={14} className="mr-1" /> Déconnecter</Button>
            </div>
          : <Button onClick={connect} className="rounded-full bg-ciel text-white hover:bg-sky-700" data-testid="drive-connect"><HardDrive size={15} className="mr-1" /> Connecter Google Drive</Button>}
      </div>

      {status.connected && (
        <>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-mono text-xs uppercase tracking-wider text-slate-400">Fichiers archivés ({files.length})</p>
              <p className="mt-1 text-xs text-slate-500">Sous-dossiers : {SUBFOLDERS.join(" · ")}</p>
            </div>
            <div className="flex items-center gap-2">
              <select value={uploadFolder} onChange={(e) => setUploadFolder(e.target.value)}
                className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-navy focus:border-ciel focus:outline-none"
                data-testid="drive-upload-folder">
                {SUBFOLDERS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="flex cursor-pointer items-center gap-2 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800" data-testid="drive-upload-label">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Archiver un fichier
                <input type="file" className="hidden" data-testid="drive-upload-input" onChange={(e) => upload(e.target.files?.[0])} />
              </label>
            </div>
          </div>
          <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
            {loadingFiles ? (
              <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ciel" /></div>
            ) : files.length === 0 ? (
              <p className="p-8 text-center text-sm text-slate-400" data-testid="drive-empty"><FolderOpen size={22} className="mx-auto mb-2 text-slate-300" />Aucun fichier archivé pour l'instant. Devis & ventes, factures & finances, contrats RH, rapports et documents patients seront rangés dans leurs sous-dossiers.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="p-3 text-left">Nom</th><th className="p-3 text-left">Dossier</th><th className="p-3 text-left">Modifié</th><th className="p-3 text-right">Ouvrir</th></tr></thead>
                <tbody>
                  {files.map((f) => (
                    <tr key={f.id} className="border-t border-slate-100" data-testid={`drive-file-${f.id}`}>
                      <td className="p-3 text-navy">{f.name}</td>
                      <td className="p-3"><span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-ciel">{f.folder || "—"}</span></td>
                      <td className="p-3 text-xs text-slate-500">{f.modifiedTime ? new Date(f.modifiedTime).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                      <td className="p-3 text-right"><a href={f.webViewLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-ciel hover:text-navy" data-testid={`drive-open-${f.id}`}><ExternalLink size={14} /> Voir</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
